import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { NotificationAveragingEnum } from 'src/app/enums/notificationAveraging.enum';
import { NotificationLimitTypeEnum } from 'src/app/enums/notificationLimitType.enum';
import { ShiftTypeEnum } from 'src/app/enums/shiftType.enum';
import { ParameterTypeEnum } from 'src/app/enums/parameterType.enum';
import { NotificationSettings } from 'src/app/models/notificationSettings.model';
import { AuthService } from 'src/app/services';
import { NotificationSettingsService } from 'src/app/services/notificationSettings.service';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { NotificationSettingsDialogComponent } from '../../modals/notification-settings-dialog/notification-settings-dialog.component';
import { Lookup } from 'src/app/models/lookup.model';
import { ParameterLookup } from 'src/app/models/parameter.model';
import { ParameterService } from 'src/app/services/parameter.service';
import { CloneNotificationSettingsDialogComponent } from '../../modals/clone-notification-settings-dialog/clone-notification-settings-dialog.component';
import { AssignNotificationSettingsDialogComponent } from '../../modals/assign-notification-settings-dialog/assign-notification-settings-dialog.component';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { ToastService } from 'src/app/services/toast.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CommonService } from 'src/app/services/common.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { NotificationConditionType } from 'src/app/enums/notificationConditionType.enum';
import { Observable, of } from 'rxjs';
import { getNamedEnum } from 'src/app/_helpers/enum-helper';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { UserService } from 'src/app/services/user.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: false,
  selector: 'app-notification-settings',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss']
})
export class NotificationSettingsComponent extends CoreGridComponent<NotificationSettings> implements OnInit, AfterViewInit {
  userEmail: string;
  parameters: ParameterLookup[] = [];
  users: Lookup[] = [];
  organizationLevels: Lookup[] = [];

  constructor(
    protected notificationSettingsService: NotificationSettingsService,
    protected dialog: MatDialog,
    authService: AuthService,
    private parameterService: ParameterService,
    private valueConversionService: ValueConversionService,
    protected toastService: ToastService,
    commonService: CommonService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private userService: UserService,
    private organizationLevelService: OrganizationLevelService,
    private translateService: TranslateService
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    const currentUser = authService.getCurrentUser();
    this.userEmail = currentUser.userEmail;
    this.gridService = notificationSettingsService;
  }

  protected get objectId(): string {
    return RolePermissionObjects.NotificationSettings;
  }

  protected override get isGridViewEnabled(): boolean {
    return true;
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Notification';
    this.sortActive = nameOf<NotificationSettings>(x => x.name);
    this.defaultOrderBy = 'asc';
    this.searchPlaceholder = 'Search Notifications';
  }

  override initializeColumns(): void {
    this.additionalActions = [
      { name: 'Assign', icon: 'fa fa-user-plus', action: (row: NotificationSettings) => this.assign(row) },
      { name: 'Clone', icon: 'fa fa-clone', action: (row: NotificationSettings) => this.clone(row) }
    ];
    this.columnConfigs = [
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.isEnabled),
        displayName: 'Enabled',
        cellTemplate: (ns: NotificationSettings) => ns.isEnabled ? 'Yes' : 'No'
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.name),
        displayName: 'Name of Notification',
        cellTemplate: (ns: NotificationSettings) => ns.name,
        iconCssClass: (ns: NotificationSettings) => ('fa fa-circle mr-2 sm-icon ' + (ns.isEnabled ? 'purple-text' : 'light-grey-text'))
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.parameterId),
        displayName: 'Parameter',
        cellTemplate: (ns: NotificationSettings) => this.getParameterName(ns.parameterId)
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.parameterType),
        displayName: 'Parameter Type',
        cellTemplate: (ns: NotificationSettings) => ns.parameterType != null ? this.commonService.getEnumName(ParameterTypeEnum, ns.parameterType) : '-',
        enumFilterMap: this.enumToValueNameArray(ParameterTypeEnum)
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.averagingType),
        displayName: 'Averaging Type',
        cellTemplate: (ns: NotificationSettings) => this.commonService.getEnumName(NotificationAveragingEnum, ns.averagingType),
        enumFilterMap: this.enumToValueNameArray(NotificationAveragingEnum)
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.shiftType),
        displayName: 'Shift Type',
        cellTemplate: (ns: NotificationSettings) => ns.shiftType != null ? this.commonService.getEnumName(ShiftTypeEnum, ns.shiftType) : '-',
        enumFilterMap: this.enumToValueNameArray(ShiftTypeEnum)
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.limit),
        displayName: 'Limit',
        cellTemplate: (ns: NotificationSettings) => ns.limit?.toString() || '-'
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.limitType),
        displayName: 'Limit Type',
        cellTemplate: (ns: NotificationSettings) => ns.limitType != null ? this.commonService.getEnumName(NotificationLimitTypeEnum, ns.limitType) : '-',
        enumFilterMap: this.enumToValueNameArray(NotificationLimitTypeEnum)
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.conditionType),
        displayName: 'Condition',
        cellTemplate: (ns: NotificationSettings) => this.commonService.getEnumName(NotificationConditionType, ns.conditionType),
        enumFilterMap: this.enumToValueNameArray(NotificationConditionType)
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.timePeriod),
        displayName: 'Averaging Duration',
        cellTemplate: (ns: NotificationSettings) => this.getAveragingPeriod(ns.timePeriod)
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.threshold),
        displayName: 'Threshold',
        cellTemplate: (ns: NotificationSettings) => this.getThresholdValue(ns)
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.createdByUserId),
        displayName: 'Created By',
        cellTemplate: (ns: NotificationSettings) => this.users?.find(u => u.id.toString() === ns.createdByUserId)?.name || '-'
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.subscribers),
        displayName: 'Subscribed',
        cellTemplate: (ns) => '',
        iconCssClass: (ns: NotificationSettings) => this.isTextMuted(ns) ? 'fas fa-minus-circle red-text big-icon' : 'fas fa-check-circle green-text big-icon'
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.applyFogCorrection),
        displayName: 'Apply Fog Correction',
        cellTemplate: (ns: NotificationSettings) => ns.applyFogCorrection ? 'Yes' : 'No'
      }),
      new ColumnConfig<NotificationSettings>({
        name: nameOf<NotificationSettings>(x => x.organizationLevelId),
        displayName: 'Visibility',
        cellTemplate: (ns: NotificationSettings) => {
          const orgLevel = this.organizationLevels.find(o => o.id === ns.organizationLevelId);
          return orgLevel ? orgLevel.name : '-';
        }
      }),
      { name: 'action', displayName: 'Action' }
    ];
  }

  get hasParameterFilter(): boolean {
    return true;
  }

  get hasOrgFilter(): boolean {
    return true;
  }

  override objectFilterMap(propertyName: string): any | undefined {
    switch (propertyName) {
      case nameOf<NotificationSettings>(x => x.parameterId):
        return this.parameterService;
      case nameOf<NotificationSettings>(x => x.createdByUserId):
        return this.userService;
      case nameOf<NotificationSettings>(x => x.organizationLevelId):
        return this.organizationLevelService;
      default:
        return undefined;
    }
  }

  ngOnInit(): void {
    super.ngOnInit();

    forkJoin({
      parameters: this.parameterService.getAll(""),
      users: this.userService.getAll(),
      organizationLevels: this.organizationLevelService.getAll()
    }).subscribe(results => {
      this.parameters = results.parameters;
      this.users = results.users;
      this.organizationLevels = results.organizationLevels;
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override initializeTabs(): { routerLink: string; title: string; }[] {
    return [
      { routerLink: '/dashboard/notification-settings', title: 'Notification Settings' }
    ];
  }

  isTextMuted(notificationSettings: NotificationSettings) {
    return !notificationSettings || !notificationSettings.subscribedEmails || !notificationSettings.subscribedEmails.includes(this.userEmail);
  }

  getParameterName(id: string): string {
    const param = this.parameters.find(x => x.id == id);
    if (!param) return '';
    return param.isNotificationOnly ? `${param.name} (${this.translateService.instant('Notification Only')})` : param.name;
  }

  getAveragingPeriod(averagingPeriod: number): string {
    return this.valueConversionService.getAveragingPeriod(averagingPeriod);
  }

  getThresholdValue(notificationSetting: NotificationSettings): string {
    const { threshold, parameterType } = notificationSetting;
    const unit = this.valueConversionService.getParameterUnit(parameterType);
    return `${threshold} ${unit}`;
  }

  assign(notificationSettings: NotificationSettings): void {
    const dialogRef = this.dialog.open(AssignNotificationSettingsDialogComponent, {
      data: { id: notificationSettings.id },
      width: '450px',
      disableClose: true,
      panelClass: 'custom-overlay-pane-class'
    });
    this.dialogClose(dialogRef);
  }

  clone(notificationSettings: NotificationSettings): void {
    const dialogRef = this.dialog.open(CloneNotificationSettingsDialogComponent, {
      data: { id: notificationSettings.id },
      width: '550px',
      disableClose: true
    });
    this.dialogClose(dialogRef);
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(entityId?: string): void {
    this.openNotificationDialog(entityId);
  }

  override openEntityDeleteDialog(entity: NotificationSettings): void {
    this.openNotificationDeleteDialog(entity);
  }

  openNotificationDeleteDialog(entity: NotificationSettings): void {
    this.openDeleteDialog(DeleteDialog, entity, this.notificationSettingsService, 'Notification Settings');
  }

  openNotificationDialog(entityId?: string): void {
    const dialogRef = this.dialog.open(NotificationSettingsDialogComponent, {
      data: { id: entityId },
      width: '992px',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result: NotificationSettings) => {
      if (!entityId && result) {
        this.assign(result);
      } else {
        this.loadPage();
      }
    });
  }

  dialogClose(dialogRef: import('@angular/material/dialog').MatDialogRef<unknown>): void {
    dialogRef.afterClosed().subscribe((result: unknown) => {
      if (result) this.loadPage();
    });
  }
}
